import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { playAlarm, toggleFullscreen } from "@/lib/tv";
import {
  CornersOut, Television, WarningDiamond, SpeakerHigh, SpeakerSlash, Broadcast, Power,
} from "@phosphor-icons/react";

const SEV_BORDER = {
  critical: "border-[#FF3B30] shadow-[0_0_0_2px_#FF3B30]",
  high: "border-[#FF9F0A] shadow-[0_0_0_2px_#FF9F0A]",
  medium: "border-[#0A84FF]",
  low: "border-white/10",
};

export default function SurveillanceWall() {
  const [cams, setCams] = useState([]);
  const [tiles, setTiles] = useState({});      // camId -> {frame, threat_level, theft, vandalism, people_count, scene_summary}
  const [alertsByCam, setAlertsByCam] = useState({});
  const [ticker, setTicker] = useState([]);
  const [auto, setAuto] = useState(true);
  const [sound, setSound] = useState(true);
  const [toggling, setToggling] = useState(false);
  const rootRef = useRef(null);
  const soundRef = useRef(true);
  const seenRef = useRef(new Set());
  soundRef.current = sound;

  const load = useCallback(async () => {
    const { data } = await api.get("/cameras");
    setCams(data.filter((c) => c.status === "online"));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fast live-frame poller (~0.7s) for smooth video.
  useEffect(() => {
    if (cams.length === 0) return;
    let cancelled = false;
    const pull = async () => {
      await Promise.all(cams.map(async (cam) => {
        try {
          const r = await api.get(`/cameras/${cam.id}/frame?display=1`);
          if (!cancelled && r.data?.b64)
            setTiles((p) => ({ ...p, [cam.id]: { ...(p[cam.id] || {}), frame: r.data.b64 } }));
        } catch { /* no frame yet */ }
      }));
    };
    pull();
    const t = setInterval(pull, 700);
    return () => { cancelled = true; clearInterval(t); };
  }, [cams]);

  // Always-on state poller — reads the 24/7 server analysis. NO per-view AI calls.
  useEffect(() => {
    let cancelled = false;
    const pull = async () => {
      try {
        const { data } = await api.get("/wall/state");
        if (cancelled) return;
        setAuto(data.auto);
        setTiles((p) => {
          const next = { ...p };
          (data.tiles || []).forEach((t) => { next[t.id] = { ...(next[t.id] || {}), ...t }; });
          return next;
        });
        const byCam = {};
        (data.alerts || []).forEach((a) => { if (!byCam[a.camera_id]) byCam[a.camera_id] = a; });
        setAlertsByCam(byCam);
        if ((data.alerts || []).length) {
          setTicker(data.alerts.slice(0, 40));
          data.alerts.forEach((a) => {
            if (seenRef.current.has(a.id + a.ts)) return;
            seenRef.current.add(a.id + a.ts);
            const hard = a.severity === "critical" || a.severity === "high";
            if (hard && soundRef.current) playAlarm(a.severity);
          });
        } else {
          setTicker([]);
        }
      } catch { /* keep last state */ }
    };
    pull();
    const t = setInterval(pull, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const toggleAuto = async () => {
    const nv = !auto;
    setToggling(true);
    try {
      await api.post("/settings/surveillance", { auto: nv });
      setAuto(nv);
      toast[nv ? "success" : "warning"](
        nv ? "24/7 AI monitoring ON — runs on the server even with this screen closed"
           : "24/7 AI monitoring PAUSED — no cameras are being analyzed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Master only");
    } finally { setToggling(false); }
  };

  const gridCols = cams.length <= 1 ? "grid-cols-1" : cams.length <= 4 ? "grid-cols-1 sm:grid-cols-2"
    : cams.length <= 9 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";

  return (
    <div ref={rootRef} className="relative bg-[#0A0A0C] min-h-[calc(100vh-8rem)]" data-testid="surveillance-wall">
      <span className="absolute bottom-1 right-2 z-10 text-[9px] font-mono-data tracking-[0.2em] text-[#3f3f46] uppercase pointer-events-none">Powered by A V Tech</span>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-3 bg-[#121214] border border-white/10 p-3">
        <span className="flex items-center gap-2 font-display font-black uppercase tracking-tighter text-sm">
          <Television size={18} weight="bold" className="text-[#007AFF]" /> Surveillance Wall
        </span>

        {/* Always-on status pill */}
        <span data-testid="wall-monitor-status"
          className={`flex items-center gap-2 px-3 py-2 font-display font-bold uppercase tracking-wide text-xs border ${auto ? "bg-[#32D74B]/15 text-[#32D74B] border-[#32D74B]/40" : "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40"}`}>
          <span className={`w-2 h-2 rounded-full ${auto ? "bg-[#32D74B] live-dot" : "bg-[#FF9F0A]"}`} />
          {auto ? <><Broadcast size={14} weight="bold" /> Monitoring 24/7</> : <>Monitoring Paused</>}
        </span>

        {/* Real 24/7 toggle (server-side) */}
        <button data-testid="wall-auto-toggle" onClick={toggleAuto} disabled={toggling}
          title="Turn the always-on server monitoring on or off (master only)"
          className="flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1E] border border-white/10 text-xs hover:border-white/30 disabled:opacity-40 transition-colors">
          <Power size={14} weight="bold" className={auto ? "text-[#FF3B30]" : "text-[#32D74B]"} />
          {auto ? "Pause monitoring" : "Resume monitoring"}
        </button>

        <button data-testid="wall-sound" onClick={() => setSound((s) => !s)} title="Toggle alarm sound"
          className="flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1E] border border-white/10 text-xs hover:border-white/30 transition-colors">
          {sound ? <SpeakerHigh size={15} className="text-[#32D74B]" /> : <SpeakerSlash size={15} className="text-[#71717A]" />}
          Alarm {sound ? "On" : "Off"}
        </button>
        <button data-testid="wall-fullscreen" onClick={() => toggleFullscreen(rootRef.current)}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 bg-[#1A1A1E] border border-white/10 text-xs hover:border-white/30 transition-colors">
          <CornersOut size={15} /> Fullscreen
        </button>
        <Link data-testid="wall-tv-mode" to="/tv"
          className="flex items-center gap-1.5 px-3 py-2 bg-[#BF5AF2]/15 border border-[#BF5AF2]/40 text-[#BF5AF2] text-xs font-display font-bold uppercase tracking-wide hover:bg-[#BF5AF2]/25 transition-colors">
          <Television size={15} weight="bold" /> TV Mode
        </Link>
      </div>

      <p className="text-[11px] text-[#71717A] font-mono-data mb-3 -mt-1 px-1" data-testid="wall-status-note">
        AI analyzes every camera automatically, around the clock — even when this screen is closed. No need to start anything.
      </p>

      {/* Grid */}
      {cams.length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-[#71717A] font-mono-data text-sm" data-testid="wall-empty">
          No online cameras. Connect a camera (Edge Agent / Camera Connect) or add a demo feed to populate the wall.
        </div>
      ) : (
        <div className={`grid ${gridCols} gap-2`} data-testid="wall-grid">
          {cams.map((cam) => {
            const t = tiles[cam.id] || {};
            const alert = alertsByCam[cam.id];
            const flagged = !!alert;
            return (
              <div key={cam.id} data-testid={`wall-tile-${cam.id}`}
                className={`relative aspect-video bg-black border overflow-hidden transition-all ${flagged ? SEV_BORDER[alert.severity] || SEV_BORDER.high : "border-white/10"}`}>
                {t.frame ? (
                  <img src={`data:image/jpeg;base64,${t.frame}`} alt={cam.name} className="w-full h-full object-cover opacity-90" />
                ) : cam.image ? (
                  <img src={cam.image} alt={cam.name} className="w-full h-full object-cover opacity-60" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[#3f3f46]"><Television size={32} weight="thin" /></div>
                )}
                <div className="absolute top-1.5 left-1.5 flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${auto ? "bg-[#32D74B] live-dot" : "bg-[#71717A]"}`} />
                  <span className="font-mono-data text-[10px] tracking-wide bg-black/50 px-1.5 py-0.5">{cam.name}</span>
                </div>
                {flagged && (
                  <div className="absolute top-1.5 right-1.5 flex items-center gap-1 bg-[#FF3B30] px-1.5 py-0.5 animate-pulse">
                    <WarningDiamond size={11} weight="fill" />
                    <span className="font-mono-data text-[10px] font-bold uppercase">{alert.type}</span>
                  </div>
                )}
                {(t.theft != null || t.people_count != null) && (
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/90 to-transparent flex gap-2 font-mono-data text-[10px]">
                    <span style={{ color: (t.theft ?? 0) >= 65 ? "#FF3B30" : "#71717A" }}>T{t.theft ?? 0}</span>
                    <span style={{ color: (t.vandalism ?? 0) >= 65 ? "#FF3B30" : "#71717A" }}>V{t.vandalism ?? 0}</span>
                    <span className="text-[#71717A]">P{t.people_count ?? 0}</span>
                    {!t.live && <span className="text-[#71717A] ml-auto">NO SIGNAL</span>}
                  </div>
                )}
                <span className="absolute bottom-1 right-2 z-10 font-display font-bold text-[11px] tracking-[0.2em] uppercase text-[#0A84FF]" style={{ textShadow: "0 1px 3px #000" }}>Powered by A V Tech</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Ticker */}
      <div className="mt-3 bg-[#121214] border border-white/10 overflow-hidden" data-testid="wall-ticker">
        <div className="flex items-center">
          <span className="shrink-0 bg-[#FF3B30] text-white font-display font-black uppercase text-xs px-3 py-2 tracking-wide">Alerts</span>
          <div className="flex-1 overflow-hidden whitespace-nowrap py-2">
            {ticker.length === 0 ? (
              <span className="px-4 font-mono-data text-xs text-[#71717A]">Surveillance quiet — no open alerts.</span>
            ) : (
              <div className="inline-block animate-[marquee_30s_linear_infinite] whitespace-nowrap">
                {ticker.map((a) => (
                  <span key={a.id} className="mx-6 font-mono-data text-xs">
                    <span style={{ color: a.severity === "critical" || a.severity === "high" ? "#FF3B30" : "#FF9F0A" }}>▲ {a.type}</span>
                    <span className="text-[#A1A1AA]"> · {a.camera} · {a.message}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Branding */}
      <p className="text-center mt-3 font-display font-bold tracking-[0.3em] uppercase text-sm text-[#0A84FF]" data-testid="wall-branding">
        Powered by A V Tech
      </p>
    </div>
  );
}
